import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useNotificationApi } from '../api';
import { notificationsKeys } from '../notifications-query-key-factory';
import { NotificationPeerTypes, NotificationResponse } from '../types/notifications.types';

type PeerBaseNotification = {
  store_item_id: string;
};

interface ParticipantNotification extends PeerBaseNotification {
  participant_id: string;
}

interface TeamNotification extends PeerBaseNotification {
  team_id: string;
}

type PeerNotificationRequest<T extends NotificationPeerTypes> = T extends 'participants'
  ? ParticipantNotification
  : T extends 'teams'
    ? TeamNotification
    : never;

type CreatePeerNotificationRequest<T extends NotificationPeerTypes> = {
  causeId: string;
  notification: PeerNotificationRequest<T>;
};

const useCreatePeerNotification = <T extends NotificationPeerTypes>({
  peerType,
}: {
  peerType: T;
}) => {
  const NotificationsApi = useNotificationApi();
  const queryClient = useQueryClient();

  const createPeerNotification = async ({
    causeId,
    notification,
  }: CreatePeerNotificationRequest<T>) => {
    const { data } = await NotificationsApi.post<NotificationResponse>(
      `/notifications/${peerType}?cause_id=${causeId}`,
      {
        cause_id: causeId,
        notification,
      }
    );
    return data.notification;
  };

  return useMutation({
    mutationFn: createPeerNotification,
    onSuccess(_, variables) {
      const { causeId, notification } = variables;
      const peerId =
        peerType === 'participants'
          ? (notification as ParticipantNotification).participant_id
          : (notification as TeamNotification).team_id;
      const key = notificationsKeys.id(causeId)[peerType].id(peerId).all;
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

export { useCreatePeerNotification };
